import "./ConfigColumn.scss";
import { Column, MetricsTablePreset } from "@blisspointmedia/bpm-types/dist/MetricsTable";
import { CustomMetric } from "@blisspointmedia/bpm-types/dist/pgTables/CustomMetric";
import { DELIVERY_COLS_AND_METRICS } from "@blisspointmedia/bpm-types/dist/Columns";
import { GLOBAL_KPI } from "./CustomMetricsModal";
import { KpiInfo } from "@blisspointmedia/bpm-types/dist/Kpis";
import { MdAdd, MdContentCopy, MdDelete, MdEdit, MdMoreVert } from "react-icons/md";
import { metricDecimalMap, resolveDecimals } from "../../utils/metric-format-utils";
import { MoveColumn, DeleteColumn, COLUMN_DND_TYPE } from "./configUtils";
import { Popover, Button } from "react-bootstrap";
import { ReactComponent as RenameIcon } from "../RenameIcon.svg";
import { roleMap } from "../../UserAdmin/UserAdmin";
import { SingleChannelMediaTypes } from "../../Performance/performanceUtils";
import { StateSetter } from "../../utils/types";
import { StreamingColumnCategory } from "@blisspointmedia/bpm-types/dist/Performance";
import { useDrag, useDrop } from "react-dnd";
import * as CC from "@blisspointmedia/bpm-types/dist/CrossChannelPerformance";
import * as CCM from "@blisspointmedia/bpm-types/dist/CrossChannel";
import * as L from "@blisspointmedia/bpm-types/dist/LinearPerformance";
import * as R from "ramda";
import * as S from "@blisspointmedia/bpm-types/dist/StreamingPerformance";
import * as Y from "@blisspointmedia/bpm-types/dist/YoutubePerformance";
import cn from "classnames";
import ColumnTop from "./ColumnTop";
import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  Button as BPMButton,
  ButtonFrameworkVariant,
  ButtonType,
  CheckBox,
  Dropdown,
  OnBlurInput,
  OverlayTrigger,
  InfoTooltip,
} from "../../Components";
import {
  ColumnMetaData,
  ColumnMetaDataMap,
  convertKPIMapToOptions,
} from "../MetricsTable/metricsTableUtils";

const BASIC_COLUMN_TYPES_MAP = {
  cross_channel: CC.BASIC_COLUMN_TYPES,
  linear: L.BASIC_COLUMN_TYPES,
  streaming: S.BASIC_COLUMN_TYPES,
  youtube: Y.BASIC_COLUMN_TYPES,
};

interface OnColumnChange {
  <T extends keyof Column>(i: number, key: T, value: Column[T]): void;
}

interface ConfigColumnProps {
  addColumn: (i: number) => void;
  addCustomMetric: () => void;
  color?: string;
  column: Column;
  columnMetaDataMap: ColumnMetaDataMap;
  deleteColumn: DeleteColumn;
  deleteCustomMetric: (customMetric: CustomMetric) => void;
  duplicateCustomMetric: (customMetric: CustomMetric) => void;
  editCustomMetric: (customMetric: CustomMetric) => void;
  globalKpi: string;
  hasSuperHeader: boolean;
  hoverItem?: string;
  index: number;
  isOnlyColumn: boolean;
  kpiMetaData: Record<string, KpiInfo>;
  moveColumn: MoveColumn;
  onSuperHeaderAdd: () => void;
  prefix: SingleChannelMediaTypes | "cross_channel";
  renameCustomMetric: (customMetric: CustomMetric) => void;
  setPresetChanges: StateSetter<MetricsTablePreset>;
}

const ConfigColumn = ({
  addColumn,
  addCustomMetric,
  color,
  column,
  columnMetaDataMap = {},
  deleteColumn,
  deleteCustomMetric,
  duplicateCustomMetric,
  editCustomMetric,
  globalKpi,
  hasSuperHeader,
  hoverItem,
  index,
  isOnlyColumn,
  kpiMetaData = {},
  moveColumn,
  onSuperHeaderAdd,
  prefix,
  renameCustomMetric,
  setPresetChanges,
}: ConfigColumnProps): JSX.Element => {
  const {
    audience,
    dataVarName: dataVarNameExtended,
    decimals,
    divider,
    heatMapping,
    id,
    kpi,
    label,
    lag,
    role,
  } = column;
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState<boolean>(false);
  const [selectedOptionTrigger, setSelectedOptionTrigger] = useState<number | undefined>();
  const columnRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const dataVarName = useMemo(() => {
    const temp = dataVarNameExtended.replace("Native", "");
    return temp.replace("_native", "");
  }, [dataVarNameExtended]);

  const kpiOptions = useMemo(() => convertKPIMapToOptions(prefix, kpiMetaData), [
    kpiMetaData,
    prefix,
  ]);

  const customMetricHasAllHardcodedKPIs = useMemo(() => {
    const metadata = columnMetaDataMap[dataVarName]
      ? { ...columnMetaDataMap[dataVarName] }
      : columnMetaDataMap[R.keys(columnMetaDataMap)[0]];
    if (!metadata.isCustom) {
      return false;
    }
    let customMetricHasAllHardcodedKPIs = true;
    if (metadata && metadata.customMetaData && metadata.customMetaData.kpis) {
      for (const kpiCol of metadata.customMetaData.kpis) {
        if (kpiCol.kpi === GLOBAL_KPI) {
          customMetricHasAllHardcodedKPIs = false;
          break;
        }
      }
    }
    return customMetricHasAllHardcodedKPIs;
  }, [columnMetaDataMap, dataVarName]);

  const [isSpecial, isComputedCurrencyCol, metadata, category] = useMemo(() => {
    const metadata = columnMetaDataMap[dataVarName]
      ? { ...columnMetaDataMap[dataVarName] }
      : columnMetaDataMap[R.keys(columnMetaDataMap)[0]];

    const basicColumnTypes = R.defaultTo(
      [],
      BASIC_COLUMN_TYPES_MAP[prefix] || DELIVERY_COLS_AND_METRICS
    );
    // https://github.com/microsoft/TypeScript/issues/26255
    const isSpecial = !(basicColumnTypes as readonly string[]).includes(dataVarName);
    const isComputedCurrencyCol = (CC.COMPUTED_CURRENCY_DATA_TYPES as readonly string[]).includes(
      dataVarName
    );
    if (isSpecial) {
      const kpiInfo = kpiMetaData[kpi || globalKpi];
      if (kpiInfo && metadata) {
        metadata.decimals = kpiInfo.decimals;
        metadata.displayName = metadata.displayName.replace("CPX", kpiInfo.cpxName);
      }
    }
    return [isSpecial, isComputedCurrencyCol, metadata, metadata.category];
  }, [columnMetaDataMap, dataVarName, prefix, kpiMetaData, kpi, globalKpi]);

  const [{ isDragging }, drag, preview] = useDrag({
    item: { type: COLUMN_DND_TYPE, id, index },
    collect: monitor => ({ isDragging: monitor.isDragging() }),
  });

  const [{ isOver, dropI }, drop] = useDrop({
    accept: COLUMN_DND_TYPE,
    canDrop: item => item.id !== id,
    collect: monitor => ({
      isOver: monitor.isOver() && monitor.canDrop(),
      dropI: monitor.getItem()?.i,
    }),
    drop: (item: { type: typeof COLUMN_DND_TYPE; id: string }) => {
      moveColumn(item.id, column.id);
    },
  });

  drop(preview(columnRef));
  drag(handleRef);

  const defaultLabel = useMemo(() => {
    if (dataVarName.startsWith("avgRevenue")) {
      return `${kpiMetaData[kpi || globalKpi]?.revenueHeader || "Avg. Rev."} (default)`;
    }
    return `${metadata.displayName} (default)`;
  }, [dataVarName, metadata, kpiMetaData, kpi, globalKpi]);

  const onChange = useCallback<OnColumnChange>(
    (i, key, value) => {
      setPresetChanges(changes => ({
        ...changes,
        dataColumns: R.adjust(
          i,
          column => ({
            ...column,
            [key]: value,
          }),
          changes.dataColumns
        ),
      }));
    },
    [setPresetChanges]
  );

  const [categorySelectVal, categorySelectOptions] = useMemo(() => {
    const CATEGORY_OPTIONS = R.map(
      category => ({
        label: category,
        value: category,
      }),
      Object.values(StreamingColumnCategory)
    );

    return [
      category ? { value: category, label: category } : null,
      R.pipe(
        R.values,
        R.sortBy(item => item.label.toLowerCase())
      )(CATEGORY_OPTIONS),
    ];
  }, [category]);

  const addNewCustomMetricButton = (
    <BPMButton
      className="addNewCustomMetricButton"
      icon={<MdAdd />}
      size="lg"
      type={ButtonType.EMPTY}
      variant={ButtonFrameworkVariant.LEADING_ICON}
    >
      Add New Custom Metric
    </BPMButton>
  );

  const getValidTypeOptions = useCallback(
    (category = null) => {
      let columnMetaDataMapFiltered: Record<string, any> = {};
      for (let key of R.keys(columnMetaDataMap)) {
        // Native metrics are selected by currency type dropdown
        if ((CCM.NATIVE_CURRENCY_COLUMN_TYPES as readonly string[]).includes(key)) {
          continue;
        }
        const col = columnMetaDataMap[key] as ColumnMetaData;
        const categoryToFilterBy = category || categorySelectVal?.value;
        if (!categoryToFilterBy || col.category === categoryToFilterBy) {
          columnMetaDataMapFiltered[key] = columnMetaDataMap[key];
        }
        if (key === "impressions" || key === "imps") {
          // We still want the default impressions to show thousands in the label for consistensy
          // but now there is the options to switch to whole number in the config
          columnMetaDataMapFiltered[key] = {
            ...columnMetaDataMapFiltered[key],
            displayName: "Impressions",
          };
        }
      }
      const TYPE_OPTIONS = R.mapObjIndexed((val, type) => {
        const itemOptions = val.isCustom ? (
          <OverlayTrigger
            customTrigger={selectedOptionTrigger === val.customMetaData.id}
            placement="right top"
            xoffset={0}
            yoffset={9}
            overlay={
              <div className="presetOptionsPresetDropdown">
                <div className="presetOption" onClick={() => editCustomMetric(val.customMetaData)}>
                  <div className="presetOptionIcon">
                    <MdEdit />
                  </div>
                  <div className="presetOptionText">Edit</div>
                </div>
                {
                  <div
                    className="presetOption"
                    onClick={() => renameCustomMetric(val.customMetaData)}
                  >
                    <div className="presetOptionIcon">
                      <RenameIcon />
                    </div>
                    <div className="presetOptionText">Rename</div>
                  </div>
                }
                {
                  <div
                    className="presetOption"
                    onClick={() => deleteCustomMetric(val.customMetaData)}
                  >
                    <div className="presetOptionIcon">
                      <MdDelete />
                    </div>
                    <div className="presetOptionText">Delete</div>
                  </div>
                }
                <div
                  className="presetOption"
                  onClick={() => duplicateCustomMetric(val.customMetaData)}
                >
                  <div className="presetOptionIcon">
                    <MdContentCopy />
                  </div>
                  <div className="presetOptionText">Duplicate</div>
                </div>
                <div className="presetLastModifiedContainer">
                  <div className="presetLastModifiedText">Last updated:</div>
                  <div className="presetLastModifiedText">
                    {val.customMetaData.last_modified} by {val.customMetaData.last_user}
                  </div>
                </div>
              </div>
            }
          >
            <div
              className="presetOptionExpander"
              onClick={() =>
                setSelectedOptionTrigger(
                  val.customMetaData.id !== selectedOptionTrigger || R.isNil(selectedOptionTrigger)
                    ? val.customMetaData.id
                    : undefined
                )
              }
            >
              <MdMoreVert />
            </div>
          </OverlayTrigger>
        ) : undefined;
        return {
          className: `${
            val.isCustom && selectedOptionTrigger === val.customMetaData.id ? "dropdownOpen" : ""
          }`,
          itemOptions,
          label: val.displayName || type,
          value: type,
          customContent: val.isCustom ? <div className="customMetricLabel">custom</div> : <></>,
        };
      }, columnMetaDataMapFiltered);
      return TYPE_OPTIONS;
    },
    [
      categorySelectVal,
      columnMetaDataMap,
      deleteCustomMetric,
      duplicateCustomMetric,
      editCustomMetric,
      renameCustomMetric,
      selectedOptionTrigger,
    ]
  );

  const [typeSelectVal, typeSelectOptions] = useMemo(() => {
    const typeOptions = getValidTypeOptions();
    const selectedVar = typeOptions[dataVarName] ? dataVarName : R.keys(typeOptions)[0];
    if (selectedVar !== dataVarName) {
      onChange(index, "dataVarName", selectedVar as S.ColumnType | L.ColumnType | Y.ColumnType);
    }
    return [
      {
        value: selectedVar,
        label: typeOptions[selectedVar].label,
      },
      R.pipe(
        R.values,
        R.sortBy(item => item.label.toLowerCase())
      )(typeOptions),
    ];
  }, [getValidTypeOptions, dataVarName, index, onChange]);

  return (
    <div className="columnConfigOuter" ref={columnRef}>
      <div
        className={cn("columnConfig", {
          hasDivider: divider,
          isOver,
          left: isOver && dropI > index,
          right: isOver && dropI <= index,
          dragging: isDragging,
          isHoverItem: hoverItem === id,
        })}
        key={index}
      >
        <ColumnTop
          addColumn={addColumn}
          color={color}
          handleRef={handleRef}
          hasSuperHeader={hasSuperHeader}
          onSuperHeaderAdd={onSuperHeaderAdd}
          i={index}
        />
        {category && (
          <div className="formSection">
            <Dropdown
              label="Category"
              options={categorySelectOptions}
              value={categorySelectVal?.value}
              onChange={item => {
                const newTypeOptions = getValidTypeOptions(item.value);
                // If the type is no longer a valid option for the category,
                // just default to the first type within the new category.
                if (!Object.keys(newTypeOptions).includes(dataVarName)) {
                  onChange(
                    index,
                    "dataVarName",
                    Object.keys(newTypeOptions)[0] as S.ColumnType | L.ColumnType | Y.ColumnType
                  );
                }
              }}
            />
          </div>
        )}
        <div className="formSection">
          <Dropdown
            className="columnTypeDropdown"
            label="Type"
            onToggle={() => {
              if (isTypeDropdownOpen) {
                setSelectedOptionTrigger(undefined);
              }
              setIsTypeDropdownOpen(!isTypeDropdownOpen);
            }}
            footer={addNewCustomMetricButton}
            footerOnClick={() => addCustomMetric()}
            hasTicks={false}
            show={isTypeDropdownOpen}
            options={R.sortBy(
              item =>
                `${
                  R.isNil(item.itemOptions) ? "DEFAULT" : "IS_CUSTOM"
                }_${item.label.toLowerCase()}`,
              typeSelectOptions
            )}
            value={typeSelectVal.value}
            onChange={value => {
              onChange(index, "dataVarName", value as S.ColumnType | L.ColumnType | Y.ColumnType);
              setIsTypeDropdownOpen(false);
              if (
                columnMetaDataMap &&
                columnMetaDataMap[value] &&
                columnMetaDataMap[value].isCustom
              ) {
                onChange(
                  index,
                  "requiredColumns",
                  R.defaultTo([], columnMetaDataMap[value].requiredTotalsColumns)
                );
                onChange(
                  index,
                  "customColumnID",
                  R.defaultTo([], columnMetaDataMap[value].customMetaData.id)
                );
              } else {
                const basicColumnTypes = R.defaultTo(
                  [],
                  BASIC_COLUMN_TYPES_MAP[prefix] || DELIVERY_COLS_AND_METRICS
                );
                if ((basicColumnTypes as readonly string[]).includes(value)) {
                  onChange(index, "kpi", undefined);
                  onChange(index, "lag", undefined);
                  onChange(index, "audience", undefined);
                }
                onChange(index, "requiredColumns", undefined);
              }
            }}
          />
        </div>
        <div className="formSection">
          <div className="label">Label</div>
          <OnBlurInput
            size="sm"
            value={label || ""}
            placeholder={defaultLabel}
            onChange={val => onChange(index, "label", val || undefined)}
          />
        </div>
        {isSpecial && (
          <>
            <div className="basicSection kpiSection">
              {!customMetricHasAllHardcodedKPIs && (
                <Dropdown
                  label={"KPI"}
                  onChange={val => onChange(index, "kpi", val === "Global" ? undefined : val)}
                  options={[{ label: "Global", value: "Global" }, ...kpiOptions]}
                  size="sm"
                  value={R.defaultTo("Global", kpi)}
                />
              )}
            </div>
            <div className="basicSection">
              {!["linear", "youtube"].includes(prefix) && (
                <Dropdown
                  label={"Lag"}
                  onChange={val => onChange(index, "lag", val === "Global" ? undefined : val)}
                  options={["Global", ...S.LAGS] as const}
                  size="sm"
                  value={lag || "Global"}
                />
              )}
              {prefix === "linear" && (
                <Dropdown
                  label={"Audience"}
                  onChange={val => onChange(index, "audience", val === "Global" ? undefined : val)}
                  options={["Global", ...L.AUDIENCES] as const}
                  size="sm"
                  value={audience || "Global"}
                />
              )}
            </div>
          </>
        )}
        {typeSelectVal.value === "impressions" || typeSelectVal.value === "imps" ? (
          <div className="formSection">
            <div className="label">Format:</div>
            <Dropdown
              size="sm"
              value={
                (R.isNil(decimals) ? metadata.decimals : decimals) === -1 ? "raw" : "thousands"
              }
              options={[
                { value: "thousands", label: "View in thousands" },
                { value: "raw", label: "View whole number " },
              ]}
              onChange={val => {
                const labelToUse = val === "thousands" ? "Impressions (000s)" : "Impressions";
                onChange(index, "label", labelToUse);
                onChange(index, "decimals", val === "thousands" ? 0 : -1);
              }}
            />
          </div>
        ) : (
          <div className="formSection">
            <div className="label">Decimals:</div>
            <OnBlurInput
              size="sm"
              type="number"
              min="0"
              // max={`${MAX_DECIMALS}`}
              value={R.isNil(decimals) ? metadata.decimals : decimals}
              onChange={decimals => {
                let parsed = parseInt(decimals);
                onChange(
                  index,
                  "decimals",
                  isNaN(parsed) || parsed < 0 ? undefined : resolveDecimals(parsed)
                );
              }}
              placeholder={`${metadata.decimals || metricDecimalMap[dataVarName] || 0} (default)`}
            />
          </div>
        )}
        {isComputedCurrencyCol && prefix !== "commerce" && (
          <div className="formSection">
            <div className="label currencyType">
              <span>Currency Type:</span>
              <InfoTooltip size="sm">
                Select how currency values are displayed for this metric.
                <ul>
                  <li>
                    Normalized to USD (Default):{" "}
                    <span>Converts foreign currency values to USD using daily exchange rates.</span>
                  </li>
                  <li>
                    Raw:{" "}
                    <span>
                      Shows values as reported by the platform in their original currency.
                    </span>
                  </li>
                </ul>
                Important:{" "}
                <span>When we do not know a platform’s raw currency we assume it is USD.</span>
              </InfoTooltip>
            </div>
            <Dropdown
              onChange={val => {
                const metric = CCM.NORMAL_NATIVE_METRIC_MAP[typeSelectVal.value][val];
                onChange(
                  index,
                  "dataVarName",
                  metric as S.ColumnType | L.ColumnType | Y.ColumnType
                );
                onChange(index, "requiredColumns", undefined);
              }}
              options={[
                { label: "Normalized to USD", value: "normalized" },
                { label: "Raw Value", value: "native" },
              ]}
              size="sm"
              value={
                (CCM.NATIVE_CURRENCY_COLUMN_TYPES as readonly string[]).includes(
                  dataVarNameExtended
                )
                  ? "native"
                  : "normalized"
              }
            />
          </div>
        )}
        {index > 0 && (
          <div className="basicSection checkboxSection">
            <div className="label">Divider?:</div>
            <CheckBox
              checked={Boolean(divider)}
              size="sm"
              onCheck={newVal => onChange(index, "divider", newVal)}
            />
          </div>
        )}
        <div className="basicSection">
          <Dropdown
            size="sm"
            label="Role"
            value={`${R.defaultTo(10, role)}`}
            options={R.map(
              role => ({
                label: roleMap[role],
                value: role,
              }),
              R.keys(roleMap) as any[]
            )}
            onChange={val => onChange(index, "role", parseInt(val))}
          />
        </div>
        <div className="basicSection checkboxSection">
          <div className="label">Heat Mapping?:</div>
          <CheckBox
            checked={Boolean(heatMapping)}
            size="sm"
            onCheck={newVal =>
              onChange(index, "heatMapping", newVal ? { colorScheme: "diverging" } : undefined)
            }
          />
        </div>
        {heatMapping && (
          <div className="heatmappingSection">
            <div className="headerSection">
              <div className="label">Heat Mapping</div>
              <OverlayTrigger
                overlay={
                  <Popover
                    id={`${id}_heatmap_glossary_popover`}
                    className="performanceHeatmapGlossaryPopover"
                  >
                    <Popover.Title>Glossary</Popover.Title>
                    <Popover.Content>
                      <div className="glossaryItem">
                        <span>Exclude Zeros:</span>
                        <span>
                          Ignores zeros when calculating. They aren't factored into means, medians,
                          or std. dev. calculations, nor are they considered minimum or maximum
                          values.
                        </span>
                      </div>
                      <div className="glossaryItem">
                        <span>Pivot:</span>
                        <span>
                          Point at which colors change. Does not apply to "Green" color scheme.
                        </span>
                      </div>
                      <div className="glossaryItem">
                        <span>Mid Point:</span>
                        <span>
                          Half way between the maximum and minimum values (respects Std. Dev.
                          Clipping).
                        </span>
                      </div>
                      <div className="glossaryItem">
                        <span>Color:</span>
                        <span>
                          Color scheme. "Standard" goes from red to yellow, then yellow to green
                          (yellow being at the pivot). "Green" goes from white to green.
                        </span>
                      </div>
                      <div className="glossaryItem">
                        <span>Std. Dev. Clipping:</span>
                        <span>
                          Numbers that are greater or less than this number of standard deviations
                          from the mean (not pivot) will not be considered when heat mapping. This
                          is to prevent outliers from impacting the heat mapping.
                        </span>
                      </div>
                      <div className="glossaryItem">
                        <span>Minimum/Maximum:</span>
                        <span>
                          Like "Std. Dev. Clipping", but hard-coded. Numbers below the minimum and
                          above the maximum are treated as if they are those numbers. If you put a
                          maximum of 100, a value of 500 is treated as 100 for heat mapping
                          calculations.
                        </span>
                      </div>
                    </Popover.Content>
                  </Popover>
                }
              >
                <div className="glossary">(Glossary)</div>
              </OverlayTrigger>
            </div>
            {!(metadata.contentReplacement?.threshold === 0) && (
              <div className="basicSection checkboxSection">
                <div className="label">Exclude Zeros</div>
                <CheckBox
                  checked={Boolean(heatMapping.excludeZeros)}
                  size="sm"
                  onCheck={newVal =>
                    onChange(index, "heatMapping", { ...heatMapping, excludeZeros: newVal })
                  }
                />
              </div>
            )}
            <div className="basicSection">
              <Dropdown
                label="Pivot"
                onChange={val => onChange(index, "heatMapping", { ...heatMapping, midpoint: val })}
                options={["MEDIAN", "MIDPOINT", "MEAN", "CUSTOM"]}
                size="sm"
                value={heatMapping.midpoint || "MIDPOINT"}
              />
            </div>
            {heatMapping.midpoint === "CUSTOM" && (
              <div className="formSection">
                <div className="label">Custom Pivot:</div>
                <OnBlurInput
                  size="sm"
                  type="number"
                  value={heatMapping.customMidpoint || ""}
                  onChange={val => {
                    onChange(index, "heatMapping", {
                      ...R.omit(["customMidpoint"], heatMapping),
                      customMidpoint: parseFloat(val) || undefined,
                    });
                  }}
                  placeholder="Custom pivot"
                />
              </div>
            )}
            <div className="basicSection">
              <Dropdown
                size="sm"
                label="Color"
                value={heatMapping.colorScheme || "diverging"}
                options={[
                  { label: "Diverging", value: "diverging" },
                  { label: "Sequential", value: "sequential" },
                ]}
                onChange={val =>
                  onChange(index, "heatMapping", { ...heatMapping, colorScheme: val })
                }
              />
            </div>
            <div className="basicSection checkboxSection">
              <div className="label">Min Is Best?:</div>
              <CheckBox
                checked={
                  R.isNil(heatMapping.minIsBest) ? metadata.minIsBest : heatMapping.minIsBest
                }
                size="sm"
                onCheck={newVal =>
                  onChange(index, "heatMapping", {
                    ...R.omit(["minIsBest"], heatMapping),
                    minIsBest: newVal,
                  })
                }
              />
            </div>
            <div className="formSection">
              <div className="label">Std. Dev. Clipping:</div>
              <OnBlurInput
                size="sm"
                type="number"
                value={heatMapping.stdevClipping || ""}
                onChange={val => {
                  onChange(index, "heatMapping", {
                    ...R.omit(["stdevClipping"], heatMapping),
                    stdevClipping: parseFloat(val) || undefined,
                  });
                }}
                placeholder="None (default)"
              />
            </div>
            <div className="formSection">
              <div className="label">Minimum:</div>
              <OnBlurInput
                size="sm"
                type="number"
                value={heatMapping.min || ""}
                onChange={val => {
                  onChange(index, "heatMapping", {
                    ...R.omit(["min"], heatMapping),
                    min: parseFloat(val) || undefined,
                  });
                }}
                placeholder="None (default)"
              />
            </div>
            <div className="formSection">
              <div className="label">Maximum:</div>
              <OnBlurInput
                size="sm"
                type="number"
                value={heatMapping.max || ""}
                onChange={val => {
                  onChange(index, "heatMapping", {
                    ...R.omit(["max"], heatMapping),
                    max: parseFloat(val) || undefined,
                  });
                }}
                placeholder="None (default)"
              />
            </div>
          </div>
        )}
        {!isOnlyColumn && (
          <Button
            size="sm"
            variant="outline-danger"
            className="deleteButton"
            onClick={() => deleteColumn(index)}
          >
            Delete
          </Button>
        )}
      </div>
    </div>
  );
};

export default ConfigColumn;
